import { infiniteQueryOptions } from '@tanstack/react-query'
import { sumBy } from 'lodash/fp'

import { createApiMethod, type } from './utils/createApiMethod'
import { stripEmptyQueryParams } from './utils/stripEmptyQueryParams'

const LEARNPATH_MEMBERSHIPS_PAGE_SIZE = 25

/** The size the endpoint reads as "return everything in one page". */
export const LEARNPATH_MEMBERSHIPS_ALL = -1

export type LearnpathMembership = {
  description: string | null
  id: string
  mandatory: boolean
  name: string
  num_members: number
  num_members_completed: number
  recurring_id: string | null
  recurring_date: string | null
  release: 'previous' | 'current' | 'next' | 'first' | null
  deactivation_date: string | null
}

export type LearnpathMembershipStatus =
  | 'active'
  | 'scheduled'
  | 'draft'
  | 'inactive'

export type LearnpathMembershipType = 'mandatory' | 'linear' | 'certificate'

export type LearnpathMembershipsListQuery = {
  offset?: number
  size?: number
  is_member?: boolean
  created_by_me?: boolean
  status?: LearnpathMembershipStatus
  types?: LearnpathMembershipType[]
  categories?: string[]
}

export const learnpaths = {
  memberships: {
    list: createApiMethod({
      method: 'GET',
      path: '/learnpaths/memberships/:userId',
      query: type<LearnpathMembershipsListQuery>(),
      response: type<{
        learnpaths: LearnpathMembership[]
        total_learnpaths: number
      }>(),
      config: { paramsSerializer: { indexes: null } },
    }),

    assign: createApiMethod({
      method: 'POST',
      path: '/learnpaths/memberships/:userId',
      data: type<{ learnpath_ids: string[] }>(),
    }),

    unassign: createApiMethod({
      method: 'DELETE',
      path: '/learnpaths/memberships/:userId',
      data: type<{ learnpath_ids: string[] }>(),
    }),
  },
}

export const learnpathsQueries = {
  all: () => ['learnpaths'] as const,
  membershipsLists: () =>
    [...learnpathsQueries.all(), 'memberships-list'] as const,
  membershipsListsForUser: (userId: string) =>
    [...learnpathsQueries.membershipsLists(), userId] as const,
  membershipsList: (
    userId: string,
    searchQuery?: LearnpathMembershipsListQuery,
  ) => {
    const query = stripEmptyQueryParams({
      ...searchQuery,
      size: searchQuery?.size ?? LEARNPATH_MEMBERSHIPS_PAGE_SIZE,
    })

    return infiniteQueryOptions({
      queryKey: [
        ...learnpathsQueries.membershipsListsForUser(userId),
        query,
      ] as const,
      queryFn: ({ pageParam }) =>
        learnpaths.memberships.list({
          params: { userId },
          query: { ...query, offset: pageParam },
        }),
      getNextPageParam: (lastPage, allPages) => {
        const loaded = sumBy((page) => page.learnpaths.length, allPages)
        return lastPage.learnpaths.length > 0 &&
          loaded < lastPage.total_learnpaths
          ? loaded
          : undefined
      },
      initialPageParam: 0,
      placeholderData: (previousData) => previousData,
    })
  },
}
